import {
  STORE_KEY_ID_LIST,
  STORE_KEY_LIST,
  fromUTCtoZoneFormatted,
  storeGet,
} from 'mailup-react-helpers'

import {
  beRecipientAddGroup,
  beRecipientApiAssignRecipientToGroup,
  beRecipientApiGetGroupDetailsByIds,
  beRecipientApiGetRecipientGroupAssignements,
  beRecipientApiRemoveRecipientFromGroup,
  beRecipientGetListOfGroupDetails,
} from 'Api/beRecipientApi'

import {
  beRecipientApiGetGroupDetails,
  beRecipientApiGetMultipleRecipientGroupsByList,
} from 'Api/beRecipientApiTS'

import type {
  GroupAssignementsResponse,
  GroupBaseInfo,
  GroupsByIdsOut,
} from 'Types/beRecipientsApi'

import type {
  GroupInfo,
  RecipientGroupsItem,
  RecipientGroupsState,
  SearchResult,
} from 'Pages/RecipientsList/model'

import {
  fetchPagePreferences,
  updatePagePreferences,
} from 'Pages/RecipientsList/recoil/services/filters'

import { withDelay } from 'Helpers/delay'
import { addGroup } from 'Pages/RecipientsList/recoil/services/modals'

export const decorateGroups = async (
  groups: GroupBaseInfo[],
): Promise<GroupInfo[]> => {
  const ids = groups.map((g) => g.IdGroup)

  if (groups.length === 0) {
    return []
  }

  const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

  const details: GroupsByIdsOut = await beRecipientApiGetGroupDetailsByIds(ids)

  const result = groups.map((g) => ({
    Id: g.IdGroup,
    AssignmentDate: fromUTCtoZoneFormatted(
      g.AssignmentDate,
      TimeZoneCode,
      'YYYY-MM-DD HH:mm',
    ),
    Name: details.Items.find((gd) => gd.Id === g.IdGroup)?.Name ?? '-',
  }))

  return await withDelay(result, 250)
}

export const fetchRecipientGroups = async (
  idRecipient: number,
  qsParams: {
    pageNumber: number
    pageSize: number
    orderBy: 'IdGroup' | 'GroupName' | 'AssignmentDate'
    orderDescending: boolean
  },
): Promise<Partial<RecipientGroupsState>> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const { orderBy, orderDescending } =
    qsParams.orderBy === undefined && qsParams.orderDescending === undefined
      ? (await fetchPagePreferences()).groups
      : {
          orderBy: qsParams.orderBy ?? 'IdGroup',
          orderDescending: qsParams.orderDescending ?? true,
        }

  const { Groups, TotalItems }: GroupAssignementsResponse =
    await beRecipientApiGetRecipientGroupAssignements(idRecipient, {
      ...qsParams,
      idList,
      orderBy,
      orderDescending,
    })

  if (Groups.length === 0 && TotalItems > 0 && qsParams.pageNumber > 1) {
    const newPageNumber = Math.ceil(TotalItems / qsParams.pageSize)

    const retry = await beRecipientApiGetRecipientGroupAssignements(
      idRecipient,
      {
        ...qsParams,
        idList,
        pageNumber: newPageNumber,
        orderBy,
        orderDescending,
      },
    )

    const items = await decorateGroups(retry.Groups)

    await updatePagePreferences({
      groups: {
        orderBy,
        orderDescending,
      },
    })

    return {
      items,
      totalItems: retry.TotalItems,
      pageNumber: newPageNumber,
      pageSize: qsParams.pageSize,
    }
  }

  const items = await decorateGroups(Groups)

  await updatePagePreferences({
    groups: {
      orderBy,
      orderDescending,
    },
  })

  return {
    items,
    totalItems: TotalItems,
    pageNumber: qsParams.pageNumber,
    pageSize: qsParams.pageSize,
  }
}

export const addRecipientToGroup = async (
  idRecipient: number,
  idGroup: number,
): Promise<boolean> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const added: boolean = await beRecipientApiAssignRecipientToGroup(
    idRecipient,
    idGroup,
    idList,
  )

  return added
}

export const addRecipientToNewGroup = async (
  idRecipient: number,
  groupName: string,
): Promise<number> => {
  const g = await addGroup(groupName)

  await addRecipientToGroup(idRecipient, g.Id)

  return g.Id
}

export const removeRecipientFromGroup = async (
  idRecipient: number,
  idGroup: number,
): Promise<void> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  await beRecipientApiRemoveRecipientFromGroup(idRecipient, idGroup, idList)
}

export const fetchAllRecipientGroups = async (
  idRecipient: number,
): Promise<number[]> => {
  const idList: number = storeGet(STORE_KEY_ID_LIST)

  const { Groups }: GroupAssignementsResponse =
    await beRecipientApiGetRecipientGroupAssignements(idRecipient, {
      pageNumber: 1,
      pageSize: 0,

      idList,
      orderBy: 'IdGroup',
      orderDescending: true,
    })

  return Groups.map((g) => g.IdGroup)
}

export const fetchRecipientsGroups = async (
  items: SearchResult[],
): Promise<RecipientGroupsItem[]> => {
  if (items.length > 0) {
    const IdList: number = storeGet(STORE_KEY_ID_LIST)
    const recipientsGroups =
      await beRecipientApiGetMultipleRecipientGroupsByList(
        {
          groupLimit: 1,
          orderBy: 'AssignmentDate',
          orderDescending: true,
        },
        {
          IdRecipients: items.map((o) => o.Id),
          IdList,
        },
      )

    const groupsIds = Array.from(
      new Set(
        recipientsGroups.Items.reduce(
          (acc, r) => [...acc, ...r.Groups.map((g) => g.IdGroup)],
          [],
        ),
      ),
    )

    const { TimeZoneCode } = storeGet(STORE_KEY_LIST)

    const groupsDetails: GroupsByIdsOut =
      groupsIds.length > 0
        ? await beRecipientApiGetGroupDetailsByIds(groupsIds)
        : {
            Items: [],
          }

    const result: RecipientGroupsItem[] = items.map((r) => {
      const recGroups = recipientsGroups.Items.find(
        (o) => o.IdRecipient === r.Id,
      )
      const groups = recGroups?.Groups ?? []
      const totalGroups = recGroups?.TotalItems ?? 0

      return {
        idRecipient: r.Id,
        moreItems: totalGroups > 1,
        items: groups.map((g) => ({
          Id: g.IdGroup,
          Name:
            groupsDetails.Items.find((gd) => gd.Id === g.IdGroup)?.Name ?? '-',
          AssignmentDate: fromUTCtoZoneFormatted(
            g.AssignmentDate,
            TimeZoneCode,
            'YYYY-MM-DD HH:mm',
          ),
        })),
      }
    })

    return result
  }

  return []
}

export const addNewGroup = async (groupName: string): Promise<any> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const existing = await beRecipientGetListOfGroupDetails(idList, {
    searchBy: groupName,
    searchType: 'EQUALTO',
    pageSize: 1,
    pageNumber: 1,
    orderBy: '',
    orderDescending: false,
  })

  if (existing.TotalItems > 0) {
    return existing.Items[0]
  }

  const newItem = await beRecipientAddGroup({
    IdList: idList,
    Name: groupName,
    Notes: '',
    Type: 'Normal',
    Deleteable: true,
  })

  return newItem
}

export const fetchGroup = async (idGroup: number): Promise<GroupInfo> => {
  const { Id, Name } = await beRecipientApiGetGroupDetails(idGroup)

  return {
    Id,
    Name,
    AssignmentDate: '',
  }
}
